import React from "react";
import PropTypes from "prop-types";
import { Card, Empty, Flex, Spin, Tag, Typography } from "antd";

import { formatAmount, formatDate, purchaseStage } from "../../../constants/marketplaceCheckout";
import { productLabel, transactionAmount } from "../../../hooks/useMyTransactions";
import { JOB_APPLICATION_STATUS } from "../../../hooks/useMyJobApplications";

const { Text } = Typography;

/** Date d'une candidature dans l'historique : sa décision si elle a été traitée, sinon son envoi. */
const jobApplicationDate = (application) => application.decidedAt || application.createdAt;

const rowStyle = (index) => ({
    padding: "12px 0",
    borderTop: index === 0 ? "none" : "1px solid #f0f0f0"
});

/**
 * Ventes, achats et travaux (candidatures aux offres de travaux champêtres), du plus récent au plus
 * ancien. `jobApplications` est optionnel : sans lui, seules les transactions sont affichées.
 */
const RecentActivities = ({ title, transactions, jobApplications = [], loading, userId, limit }) => {
    const all = [
        ...transactions.map((transaction) => ({ kind: "transaction", date: transaction.createdAt, transaction })),
        ...jobApplications.map((application) => ({ kind: "work", date: jobApplicationDate(application), application }))
    ].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    const items = limit ? all.slice(0, limit) : all;

    return (
        <Card title={title}>
            <Spin spinning={Boolean(loading)}>
                {items.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune activité pour le moment" />
                ) : items.map((item, index) => {
                    if (item.kind === "work") {
                        const { application } = item;
                        const status = JOB_APPLICATION_STATUS[application.status]
                            || { label: application.status, color: "default" };
                        return (
                            <Flex key={`work-${application.id}`} justify="space-between" align="center" gap={12} wrap style={rowStyle(index)}>
                                <div>
                                    <Text strong>{`Travaux — ${application.offerTitle || "Offre de travaux champêtres"}`}</Text>
                                    <br />
                                    <Text type="secondary">
                                        {[application.companyName, formatDate(item.date)].filter(Boolean).join(" · ")}
                                    </Text>
                                    {application.status === "ACCEPTED" && application.decisionNote && (
                                        <>
                                            <br />
                                            <Text>{`Message du producteur : ${application.decisionNote}`}</Text>
                                        </>
                                    )}
                                </div>
                                <div style={{ textAlign: "right" }}>
                                    <Tag color="gold" style={{ marginInlineEnd: 4 }}>Travaux</Tag>
                                    <Tag color={status.color} style={{ marginInlineEnd: 0 }}>{status.label}</Tag>
                                </div>
                            </Flex>
                        );
                    }
                    const { transaction } = item;
                    const stage = purchaseStage(transaction, userId);
                    return (
                        <Flex key={transaction.agreementId} justify="space-between" align="center" gap={12} wrap style={rowStyle(index)}>
                            <div>
                                <Text strong>
                                    {`${transaction.role === "SALE" ? "Vente" : "Achat"} — ${productLabel(transaction)}`}
                                </Text>
                                <br />
                                <Text type="secondary">{formatDate(transaction.createdAt)}</Text>
                            </div>
                            <div style={{ textAlign: "right" }}>
                                <Text strong>{formatAmount(transactionAmount(transaction), transaction.currency)}</Text>
                                <br />
                                <Tag color={stage.color} style={{ marginTop: 4, marginInlineEnd: 0 }}>{stage.label}</Tag>
                            </div>
                        </Flex>
                    );
                })}
            </Spin>
        </Card>
    );
};

RecentActivities.propTypes = {
    title: PropTypes.string.isRequired,
    transactions: PropTypes.arrayOf(PropTypes.object).isRequired,
    jobApplications: PropTypes.arrayOf(PropTypes.object),
    loading: PropTypes.bool,
    userId: PropTypes.string,
    limit: PropTypes.number
};

export default RecentActivities;
